import styled, { css } from "styled-components";
import { ArweaveImage } from "../../components/ArweaveImage";
import { Avatar } from "../../components/Avatar";
import { Flex } from "../../components/Grid";
import {
  Body,
  Callout,
  Caption2,
  Footnote,
  Headline,
  Subhead,
  Title2,
} from "../../components/Typography";
import { VerifiedToken } from "../../components/VerifiedToken";
import { Creator } from "../../dropTypes";
import { IMAGE_SIZES } from "../../lib/constants";
import {
  hideAboveMobile,
  hideOnMobile,
  mediumScreenAndAbove,
  smallScreenAndBelow,
  xSmallScreen,
} from "../../lib/mediaQuery";

const UserSlugLarge = styled(Body)`
  color: var(--creator-slug-color);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  ${smallScreenAndBelow(
    css`
      display: none;
    `
  )}
`;

const UserSlugSmall = styled(Subhead)`
  color: var(--creator-slug-color);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  ${mediumScreenAndAbove(
    css`
      display: none;
    `
  )}
`;
const UserSlug = ({
  children,
  title,
}: {
  children: React.ReactNode;
  title: string;
}) => (
  <>
    <UserSlugLarge title={title}>{children}</UserSlugLarge>
    <UserSlugSmall title={title}>{children}</UserSlugSmall>
  </>
);

interface UserDetailsProps {
  name: string;
  slug?: string;
  image?: string;
  verified?: boolean;
  className?: string;
}

export const UserDetails = ({
  name,
  slug,
  image,
  verified,
  className,
}: UserDetailsProps) => (
  <Flex direction="row" flex={1} className={className} align={"center"}>
    <>
      <Flex
        css={`
          margin-right: var(--spacing-xs);
          ${smallScreenAndBelow(css`
            display: none;
          `)}
        `}
      >
        <Avatar src={image} size={60} radius={60} />
      </Flex>
      <Flex
        collapse="md"
        css={`
          margin-right: var(--spacing-xs);
        `}
      >
        <Avatar src={image} size={36} radius={36} />
      </Flex>
    </>
    <Flex direction="column" flex={1}>
      <Title2
        title={name}
        css={`
          color: rgb(var(--white-light));
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          ${smallScreenAndBelow(
            css`
              display: none;
            `
          )}
        `}
      >
        {name}
        {verified && <VerifiedToken size={18} />}
      </Title2>
      <Headline
        title={name}
        css={`
          margin-bottom: -5px;
          color: rgb(var(--white-light));
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          ${mediumScreenAndAbove(
            css`
              display: none;
            `
          )}
          ${xSmallScreen(
            css`
              max-width: 162px;
            `
          )}
        `}
      >
        {name}
        {verified && <VerifiedToken size={14} />}
      </Headline>

      {slug && <UserSlug title={slug}>@{slug}</UserSlug>}
    </Flex>
  </Flex>
);

interface Props {
  creator: Creator;
  bold?: boolean;
}

export const ThumbnailUserDetails = ({ creator }: Props) => (
  <ArtistContainer>
    <ArtistImage>
      <ArweaveImage
        alt={creator?.name}
        src={creator?.image}
        fill
        sizes={IMAGE_SIZES}
        css={`
          border-radius: 100px;
          object-fit: cover;
        `}
      />
    </ArtistImage>
    <ArtistNameAndHandle direction="column">
      <ArtistNameAndTick align={"center"}>
        <ArtistNameDesktop>{creator?.name}</ArtistNameDesktop>
        <ArtistNameMobile>{creator?.name}</ArtistNameMobile>
        {creator?.verified && <VerifiedToken />}
      </ArtistNameAndTick>
      <SlugDesktop>@{creator?.slug}</SlugDesktop>
      <SlugMobile>@{creator?.slug}</SlugMobile>
    </ArtistNameAndHandle>
  </ArtistContainer>
);

const ArtistImage = styled(Flex)`
  position: relative;
  height: 40px;
  width: 40px;
  min-width: 40px;
  margin-right: var(--spacing-xxs);
  ${xSmallScreen(
    css`
      height: 30px;
      width: 30px;
      min-width: 30px;
    `
  )};
`;

const ArtistContainer = styled(Flex)`
  flex-wrap: nowrap;
  align-items: center;
  margin-bottom: var(--spacing-xxs);
`;

const ArtistNameAndHandle = styled(Flex)`
  width: 100%;
  padding: var(--spacing-xxs) 0;
`;

const ArtistNameAndTick = styled(Flex)`
  flex-wrap: nowrap;
  align-items: center;
`;

const ArtistNameDesktop = styled(Callout)<{ bold?: boolean }>`
  ${hideOnMobile};
  max-width: 158px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ArtistNameMobile = styled(Footnote)<{ bold?: boolean }>`
  ${hideAboveMobile};
  max-width: 98px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const SlugDesktop = styled(Footnote)`
  color: var(--creator-slug-color);
  ${hideOnMobile};
`;

const SlugMobile = styled(Caption2)`
  color: var(--creator-slug-color);
  ${hideAboveMobile}
`;
